/**
 * Error tracking utilities for dashboard sections
 */

import React from 'react';
import { SectionType } from '../types/section';
import { logger } from './logger';

// Error categories for better organization
export enum ErrorCategory {
  DATA_FETCHING = 'data-fetching',
  RENDERING = 'rendering',
  USER_INTERACTION = 'user-interaction',
  DATA_PROCESSING = 'data-processing',
  VALIDATION = 'validation',
  NETWORK = 'network',
  UNKNOWN = 'unknown'
}

// Error severity levels
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Interface for structured error data
export interface TrackedError {
  sectionId: string;
  sectionType: SectionType;
  message: string;
  stack?: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  timestamp: number;
  metadata?: Record<string, any>;
  resolved?: boolean;
}

// Store errors in memory for debugging during development
const errorStore: TrackedError[] = [];

/**
 * Track a section error with detailed information
 */
export const trackSectionError = (
  sectionId: string,
  sectionType: SectionType,
  error: Error | string,
  category: ErrorCategory = ErrorCategory.UNKNOWN,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  metadata?: Record<string, any>
): string => {
  const errorId = `err-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  const trackedError: TrackedError = {
    sectionId,
    sectionType,
    message: typeof error === 'string' ? error : error.message,
    stack: typeof error === 'string' ? new Error().stack : error.stack,
    category,
    severity,
    timestamp: Date.now(),
    metadata,
    resolved: false
  };
  
  // Add to in-memory store
  errorStore.push(trackedError);
  
  // Log to logger in development
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    logger.error(
      `[${trackedError.severity.toUpperCase()}][${trackedError.category}][${trackedError.sectionType}] ${trackedError.message}`,
      'ErrorTracking',
      new Error(trackedError.message),
      { sectionId: trackedError.sectionId, metadata: trackedError.metadata }
    );
  }
  
  return errorId;
};

/**
 * Get all tracked errors
 */
export const getTrackedErrors = (): TrackedError[] => {
  return [...errorStore];
};

/**
 * Mark an error as resolved
 */
export const resolveError = (errorId: string): boolean => {
  const errorIndex = errorStore.findIndex(err => 
    `err-${err.timestamp}-${err.sectionId}`.includes(errorId)
  );
  
  if (errorIndex >= 0) {
    errorStore[errorIndex].resolved = true;
    return true;
  }
  
  return false;
};

/**
 * Get errors for a specific section
 */
export const getSectionErrors = (sectionId: string): TrackedError[] => {
  return errorStore.filter(err => err.sectionId === sectionId);
};

/**
 * Clear all resolved errors
 */
export const clearResolvedErrors = (): void => {
  const unresolvedErrors = errorStore.filter(err => !err.resolved);
  errorStore.length = 0;
  errorStore.push(...unresolvedErrors);
};

/**
 * Create an error-handling HOC for React components (non-JSX implementation)
 * This is a simpler version that doesn't use JSX to avoid build issues
 */
export const withErrorTracking = <P extends object>(
  Component: React.ComponentType<P>,
  sectionType: SectionType
): React.FC<P & { fallbackUI?: React.ReactNode }> => {
  // Define a wrapper component without using JSX directly
  const WrappedComponent: React.FC<P & { fallbackUI?: React.ReactNode }> = (props) => {
    const [error, setError] = React.useState<Error | null>(null);
    const [hasError, setHasError] = React.useState(false);
    const { fallbackUI, ...componentProps } = props;
    
    // Track errors
    React.useEffect(() => {
      if (error && 'sectionId' in componentProps) {
        trackSectionError(
          (componentProps as any).sectionId,
          sectionType,
          error,
          ErrorCategory.RENDERING,
          ErrorSeverity.ERROR
        );
      }
    }, [error, componentProps]);
    
    // Custom error handler
    const handleError = (err: Error) => {
      setError(err);
      setHasError(true);
    };
    
    try {
      // If we already have an error and fallbackUI is provided, return that
      if (hasError && fallbackUI) {
        return React.createElement(React.Fragment, null, fallbackUI);
      }
      
      // Otherwise, render the component
      return React.createElement(Component, componentProps as P);
    } catch (caughtError) {
      // Handle errors during rendering
      handleError(caughtError as Error);
      
      // Return fallback UI or default error UI
      if (fallbackUI) {
        return React.createElement(React.Fragment, null, fallbackUI);
      }
      
      // Default fallback UI using React.createElement instead of JSX
      return React.createElement(
        'div', 
        { 
          className: 'p-4 border border-red-300 bg-red-50 text-red-700 rounded-md'
        },
        [
          React.createElement(
            'p',
            { className: 'font-semibold', key: 'error-title' },
            'خطایی در نمایش این بخش رخ داده است.'
          ),
          typeof window !== 'undefined' && window.location.hostname === 'localhost' && 
            React.createElement(
              'p',
              { className: 'text-sm mt-2', key: 'error-message' },
              (caughtError as Error).message
            )
        ]
      );
    }
  };
  
  WrappedComponent.displayName = `WithErrorTracking(${Component.displayName || Component.name || 'Component'})`;
  
  return WrappedComponent;
};